/**
 * EntitySidebar Component
 * Shows detected entities with accept/reject actions
 * Now uses project-scoped backend storage via React Query hooks
 */

import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Check,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useSmartDoc,
  useUpdateEntityStatus,
  useAcceptAllEntities,
  useRejectAllEntities,
} from '@/hooks/useSmartDocs'
import { getEntityConfig, type DetectedEntity } from '@/types/document'
import { FACET_TYPE_CONFIG, type FacetCategory } from '@/types/interviewer'

interface EntitySidebarProps {
  documentId: string
  onEntityClick?: (entity: DetectedEntity) => void
}

type TabType = 'pending' | 'accepted' | 'all'

export function EntitySidebar({ documentId, onEntityClick }: EntitySidebarProps) {
  const [activeTab, setActiveTab] = useState<TabType>('pending')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['market', 'business', 'solution', 'insights']))

  // React Query hooks
  const { data: document, isLoading } = useSmartDoc(documentId)
  const updateEntityStatus = useUpdateEntityStatus()
  const acceptAllEntities = useAcceptAllEntities()
  const rejectAllEntities = useRejectAllEntities()

  const entities = document?.detectedEntities || []

  // Filter entities by tab
  const filteredEntities = useMemo(() => {
    switch (activeTab) {
      case 'pending':
        return entities.filter((e) => e.status === 'pending')
      case 'accepted':
        return entities.filter((e) => e.status === 'accepted')
      default:
        return entities
    }
  }, [entities, activeTab])

  // Group entities by category
  const groupedEntities = useMemo(() => {
    const groups: Record<FacetCategory, DetectedEntity[]> = {
      market: [],
      business: [],
      solution: [],
      insights: [],
    }

    filteredEntities.forEach((entity) => {
      const config = FACET_TYPE_CONFIG[entity.type]
      const category = config?.category || 'insights'
      groups[category].push(entity)
    })

    return groups
  }, [filteredEntities])

  const pendingCount = entities.filter((e) => e.status === 'pending').length
  const acceptedCount = entities.filter((e) => e.status === 'accepted').length

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(group)) {
        next.delete(group)
      } else {
        next.add(group)
      }
      return next
    })
  }

  const handleAcceptEntity = (entityId: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId, status: 'accepted' })
  }

  const handleRejectEntity = (entityId: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId, status: 'rejected' })
  }

  const handleAcceptAll = () => {
    acceptAllEntities.mutate(documentId)
  }

  const handleRejectAll = () => {
    rejectAllEntities.mutate(documentId)
  }

  const categoryLabels: Record<FacetCategory, string> = {
    market: 'Market',
    business: 'Business',
    solution: 'Solution',
    insights: 'Insights',
  }

  return (
    <div className="h-full flex flex-col bg-white border-l border-warm-200">
      {/* Header */}
      <div className="p-4 border-b border-warm-200">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-solution" />
          <h3 className="font-semibold text-sm">Detected Entities</h3>
        </div>
        <p className="text-xs text-text-muted">
          {isLoading ? (
            <span className="flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </span>
          ) : (
            `${entities.length} entities found`
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-warm-200 bg-warm-50">
        <button
          className={cn(
            'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            activeTab === 'pending'
              ? 'bg-white text-text-primary shadow-sm'
              : 'text-text-muted hover:text-text-secondary'
          )}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pendingCount})
        </button>
        <button
          className={cn(
            'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            activeTab === 'accepted'
              ? 'bg-white text-text-primary shadow-sm'
              : 'text-text-muted hover:text-text-secondary'
          )}
          onClick={() => setActiveTab('accepted')}
        >
          Accepted ({acceptedCount})
        </button>
        <button
          className={cn(
            'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            activeTab === 'all'
              ? 'bg-white text-text-primary shadow-sm'
              : 'text-text-muted hover:text-text-secondary'
          )}
          onClick={() => setActiveTab('all')}
        >
          All
        </button>
      </div>

      {/* Entity List */}
      <div className="flex-1 overflow-y-auto p-3">
        {filteredEntities.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {activeTab === 'pending'
                ? 'No pending entities'
                : activeTab === 'accepted'
                  ? 'No accepted entities yet'
                  : 'No entities detected'}
            </p>
          </div>
        ) : (
          Object.entries(groupedEntities).map(([category, categoryEntities]) => {
            if (categoryEntities.length === 0) return null

            const isExpanded = expandedGroups.has(category)

            return (
              <div key={category} className="mb-4">
                <button
                  className="flex items-center gap-2 w-full text-left mb-2 group"
                  onClick={() => toggleGroup(category)}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-text-muted" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-text-muted" />
                  )}
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                    {categoryLabels[category as FacetCategory]}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {categoryEntities.length}
                  </Badge>
                </button>

                {isExpanded && (
                  <div className="space-y-2 ml-5">
                    {categoryEntities.map((entity) => (
                      <EntityCard
                        key={entity.id}
                        entity={entity}
                        onAccept={() => handleAcceptEntity(entity.id)}
                        onReject={() => handleRejectEntity(entity.id)}
                        onClick={() => onEntityClick?.(entity)}
                        isUpdating={updateEntityStatus.isPending}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Footer Actions */}
      {pendingCount > 0 && activeTab === 'pending' && (
        <div className="p-3 border-t border-warm-200 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs"
            onClick={handleRejectAll}
            disabled={rejectAllEntities.isPending}
          >
            {rejectAllEntities.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : null}
            Skip All
          </Button>
          <Button
            size="sm"
            className="flex-1 text-xs"
            onClick={handleAcceptAll}
            disabled={acceptAllEntities.isPending}
          >
            {acceptAllEntities.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : null}
            Accept All ({pendingCount})
          </Button>
        </div>
      )}
    </div>
  )
}

// Entity Card Component
interface EntityCardProps {
  entity: DetectedEntity
  onAccept: () => void
  onReject: () => void
  onClick?: () => void
  isUpdating?: boolean
}

function EntityCard({ entity, onAccept, onReject, onClick, isUpdating }: EntityCardProps) {
  const config = getEntityConfig(entity.suggestedOntologyType || entity.type)
  const isPending = entity.status === 'pending'

  return (
    <Card
      className={cn(
        'p-3 transition-all cursor-pointer hover:shadow-md',
        isPending && 'border-l-2',
        entity.status === 'accepted' && 'border-l-2 border-l-solution bg-solution/5',
        entity.status === 'rejected' && 'opacity-50'
      )}
      style={{
        borderLeftColor: isPending ? '#ffc107' : undefined,
      }}
      onClick={onClick}
    >
      {/* Type Badge */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1"
          style={{ color: config.color }}
        >
          <span>{config.icon}</span>
          {config.label}
        </span>
        {entity.confidence >= 0.8 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-solution/10 text-solution">
            High confidence
          </Badge>
        )}
      </div>

      {/* Content */}
      <p className="text-sm text-text-primary mb-2 line-clamp-2">
        {entity.content}
      </p>

      {/* Evidence */}
      {entity.evidence && (
        <p className="text-xs text-text-muted italic border-l-2 border-warm-200 pl-2 mb-3 line-clamp-2">
          "{entity.evidence}"
        </p>
      )}

      {/* Actions */}
      {isPending && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-text-muted">
            {Math.round(entity.confidence * 100)}% confidence
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-text-muted hover:text-text-primary"
              onClick={(e) => {
                e.stopPropagation()
                onReject()
              }}
              disabled={isUpdating}
            >
              <X className="w-3 h-3 mr-1" />
              Skip
            </Button>
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                onAccept()
              }}
              disabled={isUpdating}
            >
              <Check className="w-3 h-3 mr-1" />
              Accept
            </Button>
          </div>
        </div>
      )}

      {entity.status === 'accepted' && (
        <div className="flex items-center gap-1 text-xs text-solution">
          <Check className="w-3 h-3" />
          Accepted
        </div>
      )}
    </Card>
  )
}
